import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  AlertTriangle,
  ClipboardCheck,
  BarChart3,
  Shield,
  Target,
  Search,
  RefreshCw,
  Users,
  TrendingUp,
  TrendingDown,
  Loader2,
  Calendar,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LineChart,
  Line,
  CartesianGrid,
} from 'recharts';
import { nonConformitiesApi, sacpmApi, auditsApi, indicatorsApi, risksApi, documentsApi, calendarApi } from '../api';

const COLORS = {
  green: '#22c55e',
  yellow: '#eab308',
  orange: '#f97316',
  red: '#ef4444',
  blue: '#3b82f6',
  purple: '#a855f7',
  gray: '#6b7280',
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    documents: 0,
    ncs: { total: 0, open: 0, by_severity: {} },
    sacpm: { total: 0, open: 0, by_type: {} },
    audits: { total_year: 0, completed: 0, planned: 0 },
    indicators: { total: 0, by_status: {} },
    risks: { total: 0, critical_high: 0, by_level: {} },
  });
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth() + 1);
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());

  useEffect(() => {
    loadAllStats();
    loadCalendarEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadCalendarEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarMonth, calendarYear]);

  const loadAllStats = async () => {
    try {
      const [ncRes, sacpmRes, auditRes, indicatorRes, riskRes, docRes] = await Promise.allSettled([
        nonConformitiesApi.stats(),
        sacpmApi.stats(),
        auditsApi.stats(),
        indicatorsApi.stats(),
        risksApi.stats(),
        documentsApi.list({ limit: 1 }),
      ]);

      setStats({
        documents: docRes.status === 'fulfilled' ? (docRes.value?.data?.length || 0) : 0,
        ncs: ncRes.status === 'fulfilled' ? ncRes.value.data : { total: 0, open: 0, by_severity: {} },
        sacpm: sacpmRes.status === 'fulfilled' ? sacpmRes.value.data : { total: 0, open: 0, by_type: {} },
        audits: auditRes.status === 'fulfilled' ? auditRes.value.data : { total_year: 0, completed: 0, planned: 0 },
        indicators: indicatorRes.status === 'fulfilled' ? indicatorRes.value.data : { total: 0, by_status: {} },
        risks: riskRes.status === 'fulfilled' ? riskRes.value.data : { total: 0, critical_high: 0, by_level: {} },
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCalendarEvents = async () => {
    try {
      const res = await calendarApi.getEvents({ year: calendarYear, month: calendarMonth });
      setCalendarEvents(res.data);
    } catch (error) {
      console.error('Error loading calendar:', error);
    }
  };

  // Prepare chart data
  const ncSeverityData = [
    { name: 'Menor', value: stats.ncs.by_severity?.minor || 0, color: COLORS.yellow },
    { name: 'Maior', value: stats.ncs.by_severity?.major || 0, color: COLORS.orange },
    { name: 'Crítica', value: stats.ncs.by_severity?.critical || 0, color: COLORS.red },
  ].filter(d => d.value > 0);

  const sacpmTypeData = [
    { name: 'Corretiva', value: stats.sacpm.by_type?.corrective || 0, color: COLORS.red },
    { name: 'Preventiva', value: stats.sacpm.by_type?.preventive || 0, color: COLORS.blue },
    { name: 'Melhoria', value: stats.sacpm.by_type?.improvement || 0, color: COLORS.green },
  ].filter(d => d.value > 0);

  const indicatorStatusData = [
    { name: 'No Alvo', value: stats.indicators.by_status?.on_track || 0, color: COLORS.green },
    { name: 'Atenção', value: stats.indicators.by_status?.attention || 0, color: COLORS.yellow },
    { name: 'Crítico', value: stats.indicators.by_status?.critical || 0, color: COLORS.red },
    { name: 'Sem Dados', value: stats.indicators.by_status?.no_data || 0, color: COLORS.gray },
  ].filter(d => d.value > 0);

  const riskLevelData = [
    { name: 'Baixo', value: stats.risks.by_level?.low || 0, fill: COLORS.green },
    { name: 'Médio', value: stats.risks.by_level?.medium || 0, fill: COLORS.yellow },
    { name: 'Alto', value: stats.risks.by_level?.high || 0, fill: COLORS.orange },
    { name: 'Crítico', value: stats.risks.by_level?.critical || 0, fill: COLORS.red },
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', boxShadow: '0 2px 8px rgba(15,23,42,0.12)', padding: '0.5rem 0.75rem', borderRadius: '0.5rem' }}>
          <p className="text-white text-sm">{`${payload[0].name}: ${payload[0].value}`}</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="animate-spin" size={48} style={{ color: '#00D4CB' }} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-gray-400 mt-1">Visão geral do sistema de gestão de qualidade</p>
        </div>
        <button onClick={loadAllStats} className="btn-secondary">
          <RefreshCw size={18} />
          Atualizar
        </button>
      </div>

      {/* Critical Alerts Section */}
      <AlertsSection stats={stats} navigate={navigate} />

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="NCs Abertas"
          value={stats.ncs.open}
          total={stats.ncs.total}
          icon={<AlertTriangle className="text-red-400" />}
          color="red"
          onClick={() => navigate('/nao-conformidades')}
        />
        <StatCard
          title="SACPM em Aberto"
          value={stats.sacpm.open}
          total={stats.sacpm.total}
          icon={<ClipboardCheck className="text-yellow-400" />}
          color="yellow"
          onClick={() => navigate('/sacpm')}
        />
        <StatCard
          title="Indicadores"
          value={stats.indicators.by_status?.on_track || 0}
          total={stats.indicators.total}
          icon={<BarChart3 className="text-green-400" />}
          color="green"
          label="No Alvo"
          onClick={() => navigate('/indicadores')}
        />
        <StatCard
          title="Riscos Críticos/Altos"
          value={stats.risks.critical_high}
          total={stats.risks.total}
          icon={<Shield className="text-orange-400" />}
          color="orange"
          onClick={() => navigate('/riscos')}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* NC by Severity */}
        <div className="card p-4">
          <h3 className="text-white font-semibold mb-4">NCs por Severidade</h3>
          {ncSeverityData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={ncSeverityData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {ncSeverityData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  formatter={(value) => <span className="text-gray-300 text-xs">{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[180px] flex items-center justify-center text-gray-500">
              Sem dados
            </div>
          )}
        </div>

        {/* SACPM by Type */}
        <div className="card p-4">
          <h3 className="text-white font-semibold mb-4">SACPM por Tipo</h3>
          {sacpmTypeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={sacpmTypeData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {sacpmTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  formatter={(value) => <span className="text-gray-300 text-xs">{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[180px] flex items-center justify-center text-gray-500">
              Sem dados
            </div>
          )}
        </div>

        {/* Indicators Status */}
        <div className="card p-4">
          <h3 className="text-white font-semibold mb-4">Status dos Indicadores</h3>
          {indicatorStatusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={indicatorStatusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {indicatorStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  formatter={(value) => <span className="text-gray-300 text-xs">{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[180px] flex items-center justify-center text-gray-500">
              Sem dados
            </div>
          )}
        </div>

        {/* Risks by Level */}
        <div className="card p-4">
          <h3 className="text-white font-semibold mb-4">Riscos por Nível</h3>
          {riskLevelData.some(d => d.value > 0) ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={riskLevelData} layout="vertical">
                <XAxis type="number" tick={{ fill: '#9ca3af', fontSize: 10 }} />
                <YAxis type="category" dataKey="name" tick={{ fill: '#9ca3af', fontSize: 10 }} width={50} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[180px] flex items-center justify-center text-gray-500">
              Sem dados
            </div>
          )}
        </div>
      </div>

      {/* Audits Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-4 flex items-center gap-4">
          <div className="p-3 bg-indigo-500/20 rounded-lg">
            <Search className="text-indigo-400" size={24} />
          </div>
          <div>
            <p className="text-gray-400 text-sm">Auditorias do Ano</p>
            <p className="text-2xl font-bold text-white">{stats.audits.total_year}</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-4">
          <div className="p-3 rounded-lg" style={{ background: 'rgba(0,212,203,0.15)' }}>
            <TrendingUp size={24} style={{ color: '#00D4CB' }} />
          </div>
          <div>
            <p className="text-gray-400 text-sm">Concluídas</p>
            <p className="text-2xl font-bold" style={{ color: '#00D4CB' }}>{stats.audits.completed}</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-4">
          <div className="p-3 bg-blue-500/20 rounded-lg">
            <TrendingDown className="text-blue-400" size={24} />
          </div>
          <div>
            <p className="text-gray-400 text-sm">Planejadas</p>
            <p className="text-2xl font-bold text-blue-400">{stats.audits.planned}</p>
          </div>
        </div>
      </div>

      {/* Calendar Section */}
      <CalendarSection
        events={calendarEvents}
        month={calendarMonth}
        year={calendarYear}
        onPrevMonth={() => {
          if (calendarMonth === 1) {
            setCalendarMonth(12);
            setCalendarYear(calendarYear - 1);
          } else {
            setCalendarMonth(calendarMonth - 1);
          }
        }}
        onNextMonth={() => {
          if (calendarMonth === 12) {
            setCalendarMonth(1);
            setCalendarYear(calendarYear + 1);
          } else {
            setCalendarMonth(calendarMonth + 1);
          }
        }}
        navigate={navigate}
      />

    </div>
  );
};

const StatCard = ({ title, value, total, icon, color, label, onClick }) => (
  <div
    onClick={onClick}
    data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    style={{
      background: '#FFFFFF',
      border: '1px solid #E2E8F0',
      borderRadius: '1rem',
      padding: '1.25rem',
      cursor: 'pointer',
      transition: 'all 0.2s',
      boxShadow: '0 1px 3px rgba(15,23,42,0.08)',
    }}
    onMouseEnter={e => { e.currentTarget.style.borderColor = '#a7bdfc'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.transform = 'none'; }}
  >
    <div className="flex justify-between items-start">
      <div>
        <p style={{ color: '#64748b', fontSize: '0.8rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{title}</p>
        <p style={{ fontSize: '2rem', fontWeight: 600, color: '#334155', marginTop: '0.25rem', lineHeight: 1 }}>{value}</p>
        {total !== undefined && (
          <p style={{ color: '#64748B', fontSize: '0.75rem', marginTop: '0.35rem' }}>
            {label || 'de'} {total} {!label && 'total'}
          </p>
        )}
      </div>
      <div className={`p-2 bg-${color}-500/20 rounded-lg`}>
        {icon}
      </div>
    </div>
  </div>
);

// Alerts Component
const AlertsSection = ({ stats, navigate }) => {
  const alerts = [];

  // Critical Risks Alert
  if (stats.risks.by_level?.critical > 0) {
    alerts.push({
      type: 'critical',
      icon: Shield,
      title: 'Riscos Críticos',
      message: `${stats.risks.by_level.critical} risco(s) com nível CRÍTICO identificado(s)`,
      action: () => navigate('/riscos'),
      actionLabel: 'Ver Riscos',
    });
  }

  // High Risks Alert
  if (stats.risks.by_level?.high > 2) {
    alerts.push({
      type: 'warning',
      icon: Shield,
      title: 'Riscos Altos',
      message: `${stats.risks.by_level.high} risco(s) com nível ALTO precisam de atenção`,
      action: () => navigate('/riscos'),
      actionLabel: 'Ver Riscos',
    });
  }

  // Critical NCs Alert
  if (stats.ncs.by_severity?.critical > 0) {
    alerts.push({
      type: 'critical',
      icon: AlertTriangle,
      title: 'NCs Críticas',
      message: `${stats.ncs.by_severity.critical} não conformidade(s) CRÍTICA(S) em aberto`,
      action: () => navigate('/nao-conformidades'),
      actionLabel: 'Ver NCs',
    });
  }

  // Many Open NCs Alert
  if (stats.ncs.open > 10) {
    alerts.push({
      type: 'warning',
      icon: AlertTriangle,
      title: 'NCs Acumuladas',
      message: `${stats.ncs.open} não conformidades em aberto aguardando tratamento`,
      action: () => navigate('/nao-conformidades'),
      actionLabel: 'Ver NCs',
    });
  }

  // Critical Indicators Alert
  if (stats.indicators.by_status?.critical > 0) {
    alerts.push({
      type: 'warning',
      icon: BarChart3,
      title: 'Indicadores Críticos',
      message: `${stats.indicators.by_status.critical} indicador(es) abaixo da meta crítica`,
      action: () => navigate('/indicadores'),
      actionLabel: 'Ver Indicadores',
    });
  }

  // SACPM Alert
  if (stats.sacpm.open > 5) {
    alerts.push({
      type: 'info',
      icon: ClipboardCheck,
      title: 'SACPM Pendentes',
      message: `${stats.sacpm.open} solicitações de ação em andamento`,
      action: () => navigate('/sacpm'),
      actionLabel: 'Ver SACPM',
    });
  }

  if (alerts.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="alerts-section">
      <h2 className="text-white font-semibold flex items-center gap-2">
        <AlertTriangle className="text-yellow-400" size={18} />
        Alertas e Pendências
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {alerts.map((alert, index) => (
          <AlertCard key={index} alert={alert} />
        ))}
      </div>
    </div>
  );
};

const AlertCard = ({ alert }) => {
  const Icon = alert.icon;
  const bgColor = alert.type === 'critical'
    ? 'bg-red-500/10 border-red-500/30 hover:bg-red-500/20'
    : alert.type === 'warning'
      ? 'bg-yellow-500/10 border-yellow-500/30 hover:bg-yellow-500/20'
      : 'bg-blue-500/10 border-blue-500/30 hover:bg-blue-500/20';

  const iconColor = alert.type === 'critical'
    ? 'text-red-400'
    : alert.type === 'warning'
      ? 'text-yellow-400'
      : 'text-blue-400';

  return (
    <div
      className={`rounded-lg border p-3 cursor-pointer transition-all ${bgColor}`}
      onClick={alert.action}
      data-testid={`alert-${alert.title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-1.5 rounded ${alert.type === 'critical' ? 'bg-red-500/20' : alert.type === 'warning' ? 'bg-yellow-500/20' : 'bg-blue-500/20'}`}>
          <Icon className={iconColor} size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className={`text-sm font-medium ${iconColor}`}>{alert.title}</h4>
          <p className="text-gray-400 text-xs mt-0.5">{alert.message}</p>
          <button className={`text-xs mt-2 ${iconColor} hover:underline`}>
            {alert.actionLabel} →
          </button>
        </div>
      </div>
    </div>
  );
};

// Calendar Section Component
const MONTH_NAMES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const CalendarSection = ({ events, month, year, onPrevMonth, onNextMonth, navigate }) => {
  const getDaysInMonth = (m, y) => new Date(y, m, 0).getDate();
  const getFirstDayOfMonth = (m, y) => new Date(y, m - 1, 1).getDay();

  const daysInMonth = getDaysInMonth(month, year);
  const firstDay = getFirstDayOfMonth(month, year);
  const days = [];

  // Add empty cells for days before the first day
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }

  // Add the days
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  const getEventsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.date === dateStr);
  };

  const getEventRoute = (event) => {
    switch (event.type) {
      case 'audit': return '/auditorias';
      case 'calibration': return '/calibracoes';
      case 'nc_deadline': return '/nao-conformidades';
      case 'swot_deadline': return '/swot';
      default: return '/';
    }
  };

  return (
    <div className="card p-4" data-testid="calendar-section">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-white font-semibold flex items-center gap-2">
          <Calendar size={18} style={{ color: '#00D4CB' }} />
          Calendário de Atividades
        </h2>
        <div className="flex items-center gap-2">
          <button onClick={onPrevMonth} className="p-1 text-gray-400 hover:text-white">
            <ChevronLeft size={20} />
          </button>
          <span className="text-white font-medium min-w-[140px] text-center">
            {MONTH_NAMES[month - 1]} {year}
          </span>
          <button onClick={onNextMonth} className="p-1 text-gray-400 hover:text-white">
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((d) => (
          <div key={d} className="text-center text-gray-500 text-xs py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((day, idx) => {
          const dayEvents = getEventsForDay(day);
          const isToday = day === new Date().getDate() && month === new Date().getMonth() + 1 && year === new Date().getFullYear();

          return (
            <div
              key={idx}
              className={`min-h-[60px] p-1 rounded ${day ? 'bg-gray-800/30' : ''} ${isToday ? 'ring-1 ring-teal-400' : ''}`}
            >
              {day && (
                <>
                  <span className={`text-xs ${isToday ? 'font-bold' : 'text-gray-400'}`} style={isToday ? { color: '#00D4CB' } : {}}>{day}</span>
                  <div className="space-y-0.5 mt-0.5">
                    {dayEvents.slice(0, 2).map((event, i) => (
                      <div
                        key={i}
                        onClick={() => navigate(getEventRoute(event))}
                        className="text-[10px] truncate px-1 py-0.5 rounded cursor-pointer hover:opacity-80"
                        style={{ backgroundColor: `${event.color}20`, color: event.color }}
                        title={event.title}
                      >
                        {event.title.substring(0, 15)}...
                      </div>
                    ))}
                    {dayEvents.length > 2 && (
                      <div className="text-[10px] text-gray-500 px-1">+{dayEvents.length - 2} mais</div>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Events List */}
      {events.length > 0 && (
        <div className="mt-4 border-t border-gray-700 pt-4">
          <h4 className="text-white text-sm font-medium mb-2">Próximos Eventos ({events.length})</h4>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {events.slice(0, 5).map((event, idx) => (
              <div
                key={idx}
                onClick={() => navigate(getEventRoute(event))}
                className="flex items-center gap-2 p-2 bg-gray-800/50 rounded cursor-pointer hover:bg-gray-700/50"
              >
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: event.color }} />
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs truncate">{event.title}</p>
                  <p className="text-gray-500 text-[10px]">{event.date}</p>
                </div>
                {event.code && <span className="text-gray-500 text-[10px] font-mono">{event.code}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
